import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import authService from '../services/authService';
import profileService from '../services/profileService';
import { getMyPermissions } from '../services/rbacService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPasswordWarning, setShowPasswordWarning] = useState(false);
  const [loginCallbacks, setLoginCallbacks] = useState([]);
  // RBAC: the caller's effective permission keys (e.g. 'dashboards.manage').
  const [permissions, setPermissions] = useState([]);

  // Load the current user's effective permissions from the RBAC endpoint.
  const loadPermissions = useCallback(async () => {
    try {
      const res = await getMyPermissions();
      setPermissions(Array.isArray(res.data?.permissions) ? res.data.permissions : []);
    } catch (e) {
      setPermissions([]); // fail-closed for gating; SUPER_ADMIN is handled by role
    }
  }, []);

  // Register a callback to be called after successful login
  const registerLoginCallback = useCallback((callback) => {
    setLoginCallbacks(prev => [...prev, callback]);
    // Return unregister function
    return () => {
      setLoginCallbacks(prev => prev.filter(cb => cb !== callback));
    };
  }, []);

  useEffect(() => {
    initializeAuth();
  }, []);

  const initializeAuth = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (token) {
        const userData = await authService.validateToken(token);
        // Store org info for non-React services
        if (userData.orgId) {
          localStorage.setItem('orgId', userData.orgId);
          localStorage.setItem('orgSlug', userData.orgSlug || '');
        }
        setUser(userData);
        setShowPasswordWarning(userData.defaultPassword);
        await loadPermissions();
      }
    } catch (error) {
      localStorage.removeItem('authToken');
    } finally {
      setLoading(false);
    }
  };

  // Establish the real session from an AuthResponse that carries a token.
  const establishSession = async (response) => {
    localStorage.setItem('authToken', response.token);
    // Store org info for non-React services (e.g., WebSocket singletons)
    // SUPER_ADMIN has no org — clear any stale org context
    if (response.orgId) {
      localStorage.setItem('orgId', response.orgId);
      localStorage.setItem('orgSlug', response.orgSlug || '');
    } else {
      localStorage.removeItem('orgId');
      localStorage.removeItem('orgSlug');
    }
    setUser(response);
    setShowPasswordWarning(response.defaultPassword);
    loadPermissions();

    // Load user preferences and apply theme after login
    try {
      const preferences = await profileService.getPreferences();
      if (preferences?.theme) {
        localStorage.setItem('theme', preferences.theme);
        // Call all registered login callbacks (e.g., theme reload)
        loginCallbacks.forEach(callback => {
          try {
            callback(preferences);
          } catch (e) {
            console.debug('Login callback error:', e);
          }
        });
      }
    } catch (prefError) {
      console.debug('Could not load preferences after login:', prefError);
    }
    return response;
  };

  const login = async (username, password, orgSlug = null, captchaToken = null) => {
    const response = await authService.login(username, password, orgSlug, captchaToken);
    // Operators receive an MFA challenge (mfaRequired / mfaEnrollRequired) with
    // a short-lived pendingToken instead of a session token — no session yet.
    if (!response?.token) return response;
    return establishSession(response);
  };

  // Operator 2FA: enroll (returns { secret, otpauthUri }) then confirm a code,
  // or verify an existing authenticator. A successful verify yields the session.
  const mfaEnroll = (pendingToken) => authService.mfaEnroll(pendingToken);
  const verifyMfaEnroll = async (pendingToken, code) =>
    establishSession(await authService.mfaVerifyEnroll(pendingToken, code));
  const verifyMfa = async (pendingToken, code) =>
    establishSession(await authService.mfaVerify(pendingToken, code));

  const switchOrg = async (orgSlug) => {
    const response = await authService.switchOrg(orgSlug);
    localStorage.setItem('authToken', response.token);
    if (response.orgId) {
      localStorage.setItem('orgId', response.orgId);
      localStorage.setItem('orgSlug', response.orgSlug || '');
    }
    setUser(response);
    loadPermissions();
    return response;
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('orgId');
    localStorage.removeItem('orgSlug');
    setUser(null);
    setPermissions([]);
    setShowPasswordWarning(false);
  };

  const dismissPasswordWarning = () => {
    setShowPasswordWarning(false);
  };

  const value = {
    user,
    loading,
    showPasswordWarning,
    login,
    mfaEnroll,
    verifyMfaEnroll,
    verifyMfa,
    logout,
    switchOrg,
    dismissPasswordWarning,
    registerLoginCallback,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN',
    isSuperAdmin: user?.role === 'SUPER_ADMIN',
    // RBAC capability map. SUPER_ADMIN is above all → always true.
    permissions,
    hasPermission: (perm) => user?.role === 'SUPER_ADMIN' || permissions.includes(perm),
    hasAnyPermission: (...perms) => user?.role === 'SUPER_ADMIN' || perms.some((p) => permissions.includes(p)),
    reloadPermissions: loadPermissions,
    /** Website-only sub-operator. Has access to the operator panel
     *  chrome but only the Website Controls section (Waitlist, Docs,
     *  Contact Inbox). */
    isWebAdmin: user?.role === 'WEB_ADMIN',
    /** Either flavour of operator — used to gate /operator/** access. */
    isOperator: user?.role === 'SUPER_ADMIN' || user?.role === 'WEB_ADMIN',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};